import { FieldArray, FieldHookConfig, useField } from 'formik'
import { useMemo, useState } from 'react'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Button } from 'blaze-ui/Button'
import { ChevronDown, ChevronUp, Trash2 } from 'blaze-ui/icons'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import Styles from './FormikArrayTextAreaInput.module.scss'
import { InputContainerProps, InputSize } from 'almanac-ui/TextInputField/Types'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { ButtonSize } from 'blaze-ui/Button/Types'
import cx from 'classnames'
import { useHover } from '@react-aria/interactions'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props {
  showMore: boolean
  onShowMore: () => void
  onShowLess: () => void
  minValue?: number
  size?: InputSize
  disabled?: boolean
}

const InputContainer = ({
  onRemove,
  isRemoveDisabled,
  removeDisabledTooltip,
  size = 'xs',
  ...inputProps
}: InputContainerProps & {
  onRemove: () => void
  isRemoveDisabled?: boolean
  removeDisabledTooltip?: string
}) => {
  const { isMobile } = useResponsiveDesign()
  const { hoverProps, isHovered } = useHover({})
  const [isFocused, setIsFocused] = useState(false)
  let buttonSize
  if (size === 'lg') {
    buttonSize = 'md'
  } else if (size === 'xl') {
    buttonSize = 'lg'
  } else {
    buttonSize = 'xs'
  }

  const showTooltip = (!isFocused && isHovered && !isMobile) || isMobile

  return (
    <div className={Styles.inputContainer} {...hoverProps}>
      {showTooltip && (
        <Tooltip label={removeDisabledTooltip} hide={!isRemoveDisabled}>
          <div className={cx(Styles.closeButton, Styles[buttonSize])}>
            <IconButton
              variant="ghost"
              size={buttonSize as ButtonSize}
              icon={Trash2}
              color="var(--dark-80)"
              isDisabled={isRemoveDisabled}
              onPress={onRemove}
            />
          </div>
        </Tooltip>
      )}
      <Input isTextarea onFocusChange={setIsFocused} size={size} {...inputProps} className={Styles.input} />
    </div>
  )
}

export const FormikArrayTextAreaInput = ({
  showMore,
  onShowLess,
  onShowMore,
  minValue = 0,
  size = 'sm',
  disabled = false,
  ...props
}: FieldHookConfig<string[]> & Props) => {
  const [field, , { setValue }] = useField(props)
  const { trackTopicAdded, trackTopicDeleted } = useOnboardingTracking()

  const values = useMemo(() => {
    return showMore ? field.value : field.value.slice(0, 5)
  }, [showMore, field.value])

  const hasMore = field.value.length > 5

  return (
    <FieldArray name={props.name}>
      {({ replace, remove }) => (
        <>
          {values.map((value, index) => (
            <InputContainer
              isRemoveDisabled={disabled || field.value.length <= minValue}
              removeDisabledTooltip="Cannot have fewer topics than posts"
              key={index}
              onRemove={() => {
                const topicText = value.trim()

                if (topicText.length > 0) {
                  trackTopicDeleted(topicText, field.name)
                }

                remove(index)
              }}
              size={size}
              name={`${field.name}.${index}`}
              value={value}
              autoFocus={value.trim().length === 0}
              disabled={disabled}
              onChange={(e) => {
                return replace(index, e.target.value)
              }}
              onBlur={(e) => {
                const topicText = e.target.value.trim()

                // Track topic addition if they actually entered text
                if (topicText.length > 0) {
                  trackTopicAdded(topicText, field.name)
                }

                // Existing logic to filter out empty values
                setValue(field.value.filter((value) => value.trim().length > 0))
              }}
            />
          ))}
          {!showMore && hasMore && (
            <div className={Styles.showMoreButton}>
              <Button variant="tertiary" size="sm" onPress={onShowMore} endIcon={ChevronDown}>
                Show {field.value.length - 5} more
              </Button>
            </div>
          )}
          {showMore && hasMore && (
            <div className={Styles.showMoreButton}>
              <Button variant="tertiary" size="sm" onPress={onShowLess} endIcon={ChevronUp}>
                Show less
              </Button>
            </div>
          )}
        </>
      )}
    </FieldArray>
  )
}
